import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { getIntlLocale, useLocale } from '@/shared/i18n';
import { PageContainer } from '@/shared/ui';

const LICENSE = {
  plan: 'Enterprise Commerce',
  seatsUsed: 18,
  seatsLimit: 25,
  monthlyPrice: 149_000,
  balance: 384_500,
  nextPaymentAt: '2026-08-20T10:00:00+03:00',
  billingPeriod: '2026-08-01/2026-08-31',
};

type PaymentEventStatus = 'scheduled' | 'paid' | 'credit';

const PAYMENT_EVENTS: Array<{
  id: string;
  date: string;
  amount: number;
  status: PaymentEventStatus;
}> = [
  { id: 'INV-2026-081', date: '2026-08-20T10:00:00+03:00', amount: 149_000, status: 'scheduled' },
  { id: 'INV-2026-080', date: '2026-07-20T10:00:00+03:00', amount: 149_000, status: 'paid' },
  { id: 'CRD-2026-014', date: '2026-07-08T12:30:00+03:00', amount: -12_500, status: 'credit' },
  { id: 'INV-2026-079', date: '2026-06-20T10:00:00+03:00', amount: 149_000, status: 'paid' },
];

function money(value: number): string {
  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(value);
}

function dateTime(value: string): string {
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export function BillingPage() {
  const { t } = useTranslation('app');
  useLocale();

  const cards = [
    {
      key: 'balance',
      label: t('billing.cards.balance'),
      value: money(LICENSE.balance),
      helper: t('billing.cards.balanceHelper'),
      icon: <AccountBalanceWalletRoundedIcon />,
      tone: 'success' as const,
    },
    {
      key: 'nextPayment',
      label: t('billing.cards.nextPayment'),
      value: dateTime(LICENSE.nextPaymentAt),
      helper: money(LICENSE.monthlyPrice),
      icon: <CalendarMonthRoundedIcon />,
      tone: 'primary' as const,
    },
    {
      key: 'license',
      label: t('billing.cards.license'),
      value: money(LICENSE.monthlyPrice),
      helper: LICENSE.plan,
      icon: <CreditCardRoundedIcon />,
      tone: 'secondary' as const,
    },
    {
      key: 'seats',
      label: t('billing.cards.seats'),
      value: `${LICENSE.seatsUsed}/${LICENSE.seatsLimit}`,
      helper: t('billing.cards.seatsHelper'),
      icon: <PaymentsRoundedIcon />,
      tone: 'info' as const,
    },
  ];

  return (
    <PageContainer title={t('billing.title')} subtitle={t('billing.subtitle')} maxWidth={1280}>
      <Stack spacing={2.5}>
        <Alert severity="info" variant="outlined">
          {t('billing.demoNotice')}
        </Alert>

        <Box
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' } }}
        >
          {cards.map((card) => (
            <Card key={card.key}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h5" noWrap sx={{ mt: 0.5 }}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Box
                    aria-hidden
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2.5,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      color: `${card.tone}.main`,
                      bgcolor: (theme) => alpha(theme.palette[card.tone].main, 0.12),
                    }}
                  >
                    {card.icon}
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }} noWrap>
                  {card.helper}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              spacing={1.5}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h6">{t('billing.license.title')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('billing.license.period', { period: LICENSE.billingPeriod })}
                </Typography>
              </Box>
              <Chip color="success" variant="outlined" label={t('billing.license.status')} />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('billing.table.invoice')}</TableCell>
                  <TableCell>{t('billing.table.date')}</TableCell>
                  <TableCell>{t('billing.table.status')}</TableCell>
                  <TableCell align="right">{t('billing.table.amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PAYMENT_EVENTS.map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ReceiptLongRoundedIcon color="action" fontSize="small" />
                        <Typography fontFamily="monospace">{event.id}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{dateTime(event.date)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={t(`billing.status.${event.status}`)} />
                    </TableCell>
                    <TableCell align="right">{money(event.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Stack>
    </PageContainer>
  );
}
